// What happens to the unpaid remainder of a part-paid medical-aid line.
//
// Schemes routinely pay less than billed, and the remainder is a real decision
// with three defensible answers. Before this it was decided silently: the
// allocate route always moved the shortfall to the patient. Here the user is
// asked, and Confirm is blocked until every shortfall has an answer.
//
// The three options are a picker, not a dropdown, because they are peers and the
// choice is consequential enough to be worth reading in full. Per CLAUDE.md §1.4
// this is a secondary picker (it refines the amount above it, not the mode of
// the whole screen), so it uses secondary / outline-secondary rather than
// competing with the primary Confirm button.

import React from "react";
import { Button, Form } from "react-bootstrap";
import { zar } from "../Accounts/lineAllocation";
import { SHORTFALL_ACTIONS } from "./useEraMatchModel";

const OPTIONS = [
  {
    action: SHORTFALL_ACTIONS.BILL_PATIENT,
    label: "Bill the patient",
    hint: "The remainder moves to the patient's side of the invoice.",
  },
  {
    action: SHORTFALL_ACTIONS.DISCOUNT,
    label: "Discount",
    hint: "The remainder comes off the medical aid. The invoice total reduces.",
  },
  {
    action: SHORTFALL_ACTIONS.LEAVE_OUTSTANDING,
    label: "Leave outstanding",
    hint: "The scheme still owes it. The line stays outstanding to chase.",
  },
];

export default function ShortfallPrompt({ shortfalls, onSetAction, standalone = true }) {
  if (shortfalls.length === 0) return null;

  return (
    <div className={standalone ? "mb-3" : "mb-0"}>
      {standalone && (
        <h6 className="mb-2 text-primary fw-bold">
          Short paid lines ({shortfalls.length})
        </h6>
      )}
      <p className="small text-muted">
        The scheme paid less than these lines were billed. Decide what happens to
        each remainder before confirming.
      </p>

      {shortfalls.map((t) => (
        <div key={t.key} className="posted-invoice-info mb-2">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <span className="fw-semibold">
              {t.invoiceNumber} {t.tariffCode || ""}
              {t.description ? (
                <span className="text-muted ms-2 small">{t.description}</span>
              ) : null}
            </span>
            <span className="acc-list-num">
              Short <strong>{zar.format(t.shortfall)}</strong> of{" "}
              {zar.format(t.maxAmount)}
            </span>
          </div>

          <div className="d-flex flex-wrap gap-2">
            {OPTIONS.map((o) => (
              <Button
                key={o.action}
                size="sm"
                style={{ minWidth: 150 }}
                variant={
                  t.shortfallAction === o.action ? "secondary" : "outline-secondary"
                }
                aria-pressed={t.shortfallAction === o.action}
                onClick={() => onSetAction(t.key, o.action)}
              >
                {o.label}
              </Button>
            ))}
          </div>

          <div className="small text-muted mt-2">
            {OPTIONS.find((o) => o.action === t.shortfallAction)?.hint ||
              "Choose one to continue."}
          </div>

          {t.shortfallAction === SHORTFALL_ACTIONS.DISCOUNT && (
            <Form.Group className="mt-2">
              <Form.Label className="small mb-1">Discount note (optional)</Form.Label>
              <Form.Control
                size="sm"
                value={t.discountNote || ""}
                placeholder="Why the shortfall is being discounted"
                onChange={(e) => onSetAction(t.key, t.shortfallAction, e.target.value)}
              />
            </Form.Group>
          )}
        </div>
      ))}
    </div>
  );
}
